import { Flex, Skeleton, Stack, Text, Button, VStack, useToast, InputGroup } from '@chakra-ui/react';
import { Account, GenderType, translateGenderType } from '@/model';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { formatDocument } from '@/utils/formatDocument';
import { useEffect } from 'react';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { InputField } from '@/components/InputField';
import { InputCpfCnpj } from '@/components/ControlledInput';
import { ModalFormData, modalFormSchema } from './schemas';
import { usePutAccountInfoMutation } from '@/services/queries/account';
import { PutAccountInfoRequest } from '@/services/request/account';
import { SelectField } from '@/components/SelectField';
import { useTranslation } from 'react-i18next';

export interface PersonalDataTabProps {
  isLoading?: boolean;
  item: Account;
}

export const PersonalDataTab = ({ isLoading, item }: PersonalDataTabProps) => {
  const { t } = useTranslation();
  const putItemMutation = usePutAccountInfoMutation();
  const {
    control,
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = useForm<ModalFormData>({
    defaultValues: {
      document: item?.document || '',
      first_name: item?.first_name || '',
      last_name: item?.last_name || '',
      birthdate: item?.birthdate || '',
      gender: item?.gender || '',
      nickname: item?.nickname || '',
    },
    resolver: zodResolver(modalFormSchema),
  });
  const toast = useToast();

  const onSubmit = (data: ModalFormData) => {
    const payload: PutAccountInfoRequest = {
      first_name: data.first_name,
      last_name: data.last_name,
      nickname: data.nickname,
      document: data.document,
      birthdate: data.birthdate,
      gender: data.gender,
    };

    putItemMutation.mutate(
      { id: item.uuid, payload },
      {
        onSuccess: () => {
          toast({
            status: 'success',
            title: t('pages.accountdetail.components.tabs.personal_data_tab.success'),
          });
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title: (resp && resp.message) || t('pages.accountdetail.components.tabs.personal_data_tab.error'),
          });
        },
      },
    );
  };

  useEffect(() => {
    if (!item) return;

    setValue('document', formatDocument(item.document || ''));
    setValue('first_name', item.first_name || '');
    setValue('last_name', item.last_name || '');
    setValue('birthdate', item.birthdate || '');
    setValue('gender', item.gender || '');
    setValue('nickname', item.nickname || '');
  }, [item, setValue]);

  return (
    <Flex direction='column' gap='1.5rem'>
      {isLoading ? (
        <Skeleton w='160px' h='21px' />
      ) : (
        <Text fontWeight='bold'>{t('pages.accountdetail.components.tabs.personal_data_tab.title')}</Text>
      )}

      <Stack flex={1}>
        <VStack as='form' gap='kilo' align='start' onSubmit={handleSubmit(onSubmit)} maxWidth={{ base: '100%', md: '30%' }}>
          {isLoading ? (
            <>
              <Skeleton width='100%' h='40px' />
              <Skeleton width='100%' h='40px' />
              <Skeleton width='100%' h='40px' />
              <Skeleton width='100%' h='40px' />
              <Skeleton width='100%' h='40px' />
              <Skeleton width='100%' h='40px' />
            </>
          ) : (
            <>
              <InputField
                withoutFloating
                label={t('pages.accountdetail.components.tabs.personal_data_tab.first_name_label')}
                rootProps={{
                  isInvalid: !!errors?.first_name,
                  errorMessage: errors?.first_name?.message,
                }}
                {...register('first_name')}
              />
              <InputField
                withoutFloating
                label={t('pages.accountdetail.components.tabs.personal_data_tab.last_name_label')}
                rootProps={{
                  isInvalid: !!errors?.last_name,
                  errorMessage: errors?.last_name?.message,
                }}
                {...register('last_name')}
              />
              <InputCpfCnpj
                withoutFloating
                control={control}
                name='document'
                label={t('pages.accountdetail.components.tabs.personal_data_tab.document_label')}
                rootProps={{
                  isInvalid: !!errors?.document,
                  errorMessage: errors?.document?.message,
                }}
              />

              <InputField
                withoutFloating
                label={t('pages.accountdetail.components.tabs.personal_data_tab.nickname_label')}
                rootProps={{
                  isInvalid: !!errors?.nickname,
                  errorMessage: errors?.nickname?.message,
                }}
                {...register('nickname')}
              />

              <InputField
                withoutFloating
                label={t('pages.accountdetail.components.tabs.personal_data_tab.birthdate_label')}
                type='date'
                fullDatePickerArea
                rootProps={{
                  isInvalid: !!errors?.birthdate,
                  errorMessage: errors?.birthdate?.message,
                }}
                {...register('birthdate')}
              />

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t('pages.accountdetail.components.tabs.personal_data_tab.gender_label')}</Text>
                <SelectField
                  defaultValue={item?.gender || ''}
                  {...register('gender')}
                  rootProps={{
                    isInvalid: !!errors?.gender,
                    errorMessage: errors?.gender?.message,
                  }}
                >
                  <option value='' disabled>
                    {t('pages.accountdetail.components.tabs.personal_data_tab.select_option')}
                  </option>
                  <option value={GenderType.NO_ANSWER}>{translateGenderType(GenderType.NO_ANSWER)}</option>
                  <option value={GenderType.OTHER}>{translateGenderType(GenderType.OTHER)}</option>
                  <option value={GenderType.FEMALE}>{translateGenderType(GenderType.FEMALE)}</option>
                  <option value={GenderType.MALE}>{translateGenderType(GenderType.MALE)}</option>
                </SelectField>
              </InputGroup>
            </>
          )}

          {isLoading ? (
            <Skeleton width='138px' h='40px' />
          ) : (
            <Button type='submit'>{t('pages.accountdetail.components.tabs.personal_data_tab.button_submit')}</Button>
          )}
        </VStack>
      </Stack>

      <CenteredSpinner isLoading={putItemMutation.isLoading} />
    </Flex>
  );
};
